import { useState } from 'react'
import { AlertCircle, CheckCircle2, Copy, Download, Loader2 } from 'lucide-react'
import { OUTPUT_PRESETS, outputPresetIdFor, type OutputPresetId } from '@shared/outputPresets'
import { useEditorStore } from '../../../store/editorStore'
import { ExportHistory } from './ExportHistory'
import { Button } from '../../common/Button'
import { getTotalDurationSec } from '../../../lib/timelineMath'

export function TabExport() {
  const composition = useEditorStore((s) => s.composition)
  const requestRender = useEditorStore((s) => s.requestRender)
  const renderJob = useEditorStore((s) => s.renderJob)
  const setOutput = useEditorStore((s) => s.setOutput)
  const [copied, setCopied] = useState<'caption' | 'hashtags' | null>(null)

  if (!composition) return null
  const durationSec = getTotalDurationSec(composition)
  const busy = renderJob?.status === 'queued' || renderJob?.status === 'running'
  const caption = composition.meta.caption ?? ''
  const hashtagLine = (composition.meta.hashtags ?? []).map((h) => `#${h}`).join(' ')
  const presetId = outputPresetIdFor(composition.output)
  const currentFps = composition.output.fps

  // P10-3 (2026-09-06): switching presets here does NOT touch a render/preview
  // Job that is already `queued`/`running` — the job handler reads the
  // Composition it was HANDED at enqueue time (server/src/render/index.ts
  // takes a `Composition` snapshot, not a live reference), so this select
  // stays enabled even while `busy` is true.
  function handlePresetChange(id: OutputPresetId) {
    const preset = OUTPUT_PRESETS.find((p) => p.id === id)
    if (!preset) return
    setOutput({ width: preset.width, height: preset.height, fps: currentFps })
  }

  async function copyText(text: string, which: 'caption' | 'hashtags') {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(which)
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 1500)
    } catch {
      // clipboard permission can legitimately be denied — don't pretend it worked.
      setCopied(null)
    }
  }

  return (
    <div className="p-3 space-y-4">
      <div className="rounded border border-ink-border bg-ink-surface2/50 p-3 space-y-2 text-xs">
        <div>
          <label className="block text-[11px] font-medium text-text-muted mb-1" htmlFor="output-preset-select">
            出力サイズ
          </label>
          <select
            id="output-preset-select"
            data-testid="output-preset"
            value={presetId === 'custom' ? 'custom' : presetId}
            onChange={(e) => handlePresetChange(e.target.value as OutputPresetId)}
            className="w-full rounded bg-ink-bg/60 border border-ink-border px-2.5 py-1.5 text-xs text-text focus:outline-none focus:border-accent-blue/60"
          >
            {presetId === 'custom' && (
              <option value="custom" disabled>
                カスタム {composition.output.width}×{composition.output.height}
              </option>
            )}
            {OUTPUT_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[10px] text-text-faint">
            全プリセット短辺1080px・テロップの大きさは共通。素材はfitとcropXで切り出し
          </p>
        </div>
        <Row k="フレームレート" v={`${composition.output.fps} fps`} />
        <Row k="推定尺" v={`${durationSec.toFixed(1)} 秒`} />
        <Row k="コーデック" v="H.264 / AAC 192k" />
        <Row k="ラウドネス" v="-14 LUFS (loudnorm)" />
      </div>

      <Button
        variant="primary"
        className="w-full"
        icon={busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        onClick={requestRender}
        disabled={busy}
        data-testid="btn-export-tab"
      >
        {busy ? '書き出し中…' : '書き出し'}
      </Button>

      {renderJob && (
        <div className="rounded border border-ink-border p-2.5 text-xs space-y-1.5" data-testid="render-job-status">
          <div className="flex items-center gap-1.5">
            {renderJob.status === 'done' && <CheckCircle2 size={13} className="text-accent-blue" />}
            {renderJob.status === 'error' && <AlertCircle size={13} className="text-accent-red" />}
            {(renderJob.status === 'running' || renderJob.status === 'queued') && (
              <Loader2 size={13} className="animate-spin text-text-muted" />
            )}
            <span>
              {renderJob.status === 'done'
                ? '完了しました'
                : renderJob.status === 'error'
                  ? `失敗: ${renderJob.error}`
                  : `処理中… ${Math.round(renderJob.progress * 100)}%`}
            </span>
          </div>
          {(renderJob.status === 'running' || renderJob.status === 'queued') && (
            <div className="h-1 rounded bg-ink-bg overflow-hidden">
              <div
                className="h-full bg-accent-blue transition-all"
                style={{ width: `${Math.round(renderJob.progress * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {(caption || hashtagLine) && (
        <div className="rounded border border-ink-border bg-ink-surface2/50 p-3 space-y-2.5" data-testid="caption-panel">
          {caption && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-medium text-text-muted">キャプション</span>
                <button
                  onClick={() => copyText(caption, 'caption')}
                  className="flex items-center gap-1 text-[10px] text-accent-blue hover:underline"
                  data-testid="copy-caption"
                >
                  <Copy size={11} />
                  {copied === 'caption' ? 'コピーしました' : 'コピー'}
                </button>
              </div>
              <p className="text-xs whitespace-pre-wrap break-words">{caption}</p>
            </div>
          )}
          {hashtagLine && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-medium text-text-muted">ハッシュタグ</span>
                <button
                  onClick={() => copyText(hashtagLine, 'hashtags')}
                  className="flex items-center gap-1 text-[10px] text-accent-blue hover:underline"
                  data-testid="copy-hashtags"
                >
                  <Copy size={11} />
                  {copied === 'hashtags' ? 'コピーしました' : 'コピー'}
                </button>
              </div>
              <p className="text-xs text-text-muted break-words">{hashtagLine}</p>
            </div>
          )}
        </div>
      )}

      <ExportHistory />
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-text-faint">{k}</span>
      <span>{v}</span>
    </div>
  )
}
